import { ApolloClient, createHttpLink, gql, InMemoryCache } from '@apollo/client';
import { persistCache, SessionStorageWrapper } from 'apollo3-cache-persist';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';

import keycloak from '../keycloak/keycloak';
import { checkRefreshToken } from '../keycloak/keycloakUtils';
import { apiCallFailed } from '../api/apiCallActions';
import { HTTP_RESPONSE_CODE } from '../../constants/http';
import { dataDogAddWarning } from '../logging/datadog';

export const RETRY_CONFIG = {
    max: 3,
    initialDelayMs: 1000,
    maxDelayMs: 4000,
    jitter: true,
};

// Skip retry for explicit cancellations and 4xx — those won't recover.
export const isRetriableNetworkError = error => {
    if (!error) return false;
    if (error.name === 'AbortError') return false;
    const status = error.statusCode;
    if (status && status >= 400 && status < 500) return false;
    return true;
};

class ApolloServiceClient {
    constructor() {
        this.store = null;
    }

    setStore(store) {
        this.store = store;
    }

    async initApolloClient() {
        const httpLink = createHttpLink({
            uri:
                process.env.NODE_ENV === 'development'
                    ? 'https://mclass-enrollment.aws.wgen.net/graphql'
                    : 'https://mclass-enrollment.mclass.amplify.com/graphql',
        });
        const authLink = setContext(({ headers }) =>
            checkRefreshToken().then(() =>
                Promise.resolve({
                    headers: {
                        ...headers,
                        authorization: `Bearer ${keycloak.token}`,
                    },
                }),
            ),
        );

        const errorLink = onError(({ networkError, operation }) => {
            if (!networkError) return;

            if (networkError.statusCode === HTTP_RESPONSE_CODE.UNAUTHORIZED) {
                if (this.store) {
                    this.store.dispatch(
                        apiCallFailed(
                            'graphql',
                            { method: 'POST' },
                            'Network error occurred',
                            networkError.statusCode,
                        ),
                    );
                } else {
                    keycloak.logout();
                }
                return;
            }

            // Reached only after RetryLink has exhausted attempts. Operations
            // without a per-callsite onError rely on this fallback for telemetry.
            dataDogAddWarning('GraphQL network error', {
                operation: operation?.operationName || 'unknown',
                message: networkError.message,
                statusCode: networkError.statusCode,
            });
        });

        const retryLink = new RetryLink({
            delay: {
                initial: RETRY_CONFIG.initialDelayMs,
                max: RETRY_CONFIG.maxDelayMs,
                jitter: RETRY_CONFIG.jitter,
            },
            attempts: {
                max: RETRY_CONFIG.max,
                retryIf: isRetriableNetworkError,
            },
        });

        const cache = new InMemoryCache();
        await persistCache({
            cache,
            storage: new SessionStorageWrapper(window.sessionStorage),
        });

        return new ApolloClient({
            link: errorLink
                .concat(authLink)
                .concat(retryLink)
                .concat(httpLink),
            cache,
            defaultOptions: {
                watchQuery: {
                    errorPolicy: 'none',
                },
                query: {
                    errorPolicy: 'none',
                },
            },
        });
    }

    async getClient() {
        if (!this.client) {
            this.client = await this.initApolloClient();
        }
        return this.client;
    }
}

export default new ApolloServiceClient();

export const GET_STAFF_CONFIG = gql`
    query GetStaffConfig {
        getStaffConfig {
            licenses
        }
    }
`;

export const GET_ACCESSIBILITY_CONFIG = gql`
    query GetAccessibilityQuery($studentUids: String!) {
        getApplicationConfigResolvers_v2(studentUids: $studentUids) {
            accessibilityConfig {
                studentUid
                config {
                    zoom
                    isMazeUntimed
                    isCaptionsEnabled
                    isAnimationVisualReduced
                }
                config_bk
            }
        }
    }
`;

export const SET_ACCESSIBILITY_CONFIG = gql`
    mutation SetAccessibilityConfig(
        $configBK: String = null
        $fontSizeScaleValue: Float!
        $isCaptionsEnabled: Boolean!
        $isAnimationVisualReduced: Boolean!
        $studentUid: String!
    ) {
        postApplicationConfigResolvers_v2(
            config: {
                accessibility: {
                    zoom: $fontSizeScaleValue
                    isCaptionsEnabled: $isCaptionsEnabled
                    isAnimationVisualReduced: $isAnimationVisualReduced
                }
            }
            configBK: $configBK
            studentUid: $studentUid
        ) {
            accessibility_bk
        }
    }
`;

export const GET_INSTITUTION_INFO = gql`
    query GetInstitutionInfo($instId: String!) {
        getInstitutionInfo_v2(instId: $instId) {
            windowDates {
                fourthWindowLabel
            }
        }
    }
`;
